import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api.js';
import { courseSetLabel } from '../courseSets.js';
import Modal from './Modal.jsx';

const LICENSE_OPTIONS = ['Basis', 'Pro', 'Business', 'Enterprise'];
const EMPTY_MANDANT = {
    customer_no: '',
    member_no: '',
    name: '',
    contact: '',
    email: '',
    street: '',
    zip: '',
    city: '',
    contract: '',
    contract_start: '',
    license: 'Basis',
    course_set: 'Standard',
    notes: '',
    active: 1,
};

export default function Mandanten() {
    const [mandanten, setMandanten] = useState([]);
    const [courseSets, setCourseSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    // Toggle: aus = nur aktive (Standardsicht), an = alle inkl. inaktive.
    const [showInactive, setShowInactive] = useState(false);
    const [editing, setEditing] = useState(null);

    async function reload() {
        try {
            setError('');
            const rows = await api.listMandanten();
            setMandanten(rows);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        reload();
        // Kurssets kommen aus dem Backend (einzige Quelle der Wahrheit).
        api.listTotaraCourseSets().then(setCourseSets).catch(() => { /* unkritisch */ });
    }, []);

    const filtered = useMemo(() => {
        let rows = mandanten;
        if (!showInactive) {
            rows = rows.filter((m) => Number(m.active) === 1);
        }
        const q = search.trim().toLowerCase();
        if (q) {
            rows = rows.filter((m) =>
                [m.customer_no, m.member_no, m.name, m.contact, m.email, m.city, m.contract, m.license, m.course_set]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(q))
            );
        }
        return rows;
    }, [mandanten, search, showInactive]);

    const inactiveCount = useMemo(
        () => mandanten.filter((m) => Number(m.active) === 0).length,
        [mandanten],
    );

    async function handleDelete(id) {
        if (!window.confirm('Diesen Mandanten wirklich löschen? Verknüpfte Rechnungen werden mitgelöscht.')) return;
        try {
            await api.deleteMandant(id);
            await reload();
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;

    return (
        <div className="page-centered page-centered--wide">
            <h1>Mandanten</h1>
            {error && <div className="panel" style={{ color: 'var(--color-danger)' }}>Fehler: {error}</div>}

            <div className="toolbar">
                <input
                    type="text"
                    placeholder="Suche (Name, Kundennr., E-Mail, …)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 320 }}
                />
                <label
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                    title="Aus = nur aktive Mandanten, an = alle inkl. inaktive"
                >
                    <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        style={{ width: 'auto', margin: 0 }}
                    />
                    Inaktive einblenden
                </label>
                {inactiveCount > 0 && (
                    <span className="muted" style={{ fontSize: 12 }}>
                        {showInactive ? `${inactiveCount} inaktiv` : `${inactiveCount} inaktiv ausgeblendet`}
                    </span>
                )}
                <div className="spacer" />
                <button className="btn btn--primary" onClick={() => setEditing({ ...EMPTY_MANDANT })}>
                    Neuer Mandant
                </button>
            </div>

            <div className="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Kundennr.</th>
                            <th>Name</th>
                            <th>Kontakt</th>
                            <th>E-Mail</th>
                            <th>Ort</th>
                            <th>Vertrag</th>
                            <th>Lizenz</th>
                            <th>Kursset</th>
                            <th style={{ width: 1 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={10} className="empty">Keine Mandanten gefunden.</td></tr>
                        ) : filtered.map((m) => {
                            const isActive = Number(m.active) === 1;
                            return (
                                <tr key={m.id} className={isActive ? '' : 'row--inactive'}>
                                    <td>
                                        <span className={'status status--' + (isActive ? 'aktiv' : 'inaktiv')}>
                                            {isActive ? 'aktiv' : 'inaktiv'}
                                        </span>
                                    </td>
                                    <td>{m.customer_no || <span className="muted">–</span>}</td>
                                    <td>
                                        <Link to={`/mandanten/${m.id}`}>{m.name}</Link>
                                    </td>
                                    <td>{m.contact || <span className="muted">–</span>}</td>
                                    <td>{m.email || <span className="muted">–</span>}</td>
                                    <td>{m.city || <span className="muted">–</span>}</td>
                                    <td>{m.contract || <span className="muted">–</span>}</td>
                                    <td>{m.license || <span className="muted">–</span>}</td>
                                    <td>{m.course_set ? courseSetLabel(m.course_set) : <span className="muted">–</span>}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <button className="btn btn--sm" onClick={() => setEditing({ ...m })}>Bearbeiten</button>{' '}
                                        <button className="btn btn--sm btn--danger" onClick={() => handleDelete(m.id)}>Löschen</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {editing && (
                <MandantForm
                    initial={editing}
                    courseSets={courseSets}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await reload(); }}
                />
            )}
        </div>
    );
}

/**
 * Schnellbearbeitung als Modal – die ausführliche Bearbeitung erfolgt
 * auf der Detail-Seite (inkl. Markdown-Notizen, Rechnungen etc.).
 * Notizen werden hier bewusst NICHT angeboten, weil der Markdown-Editor
 * eine eigene Seite verdient.
 */
function MandantForm({ initial, courseSets, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    function update(field, value) {
        setData((d) => ({ ...d, [field]: value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            // notes hier nicht mitschicken – sonst würde ein leeres Modal-Feld
            // bestehende Notizen versehentlich überschreiben.
            const { notes: _ignored, ...payload } = data;
            if (data.id) {
                await api.updateMandant(data.id, payload);
            } else {
                await api.createMandant(payload);
            }
            await onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            title={data.id ? `Mandant bearbeiten: ${initial.name}` : 'Neuer Mandant'}
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn" onClick={onClose}>Abbrechen</button>
                    <button type="submit" form="mandant-form" className="btn btn--primary" disabled={saving}>
                        {saving ? 'Speichere…' : 'Speichern'}
                    </button>
                </>
            }
        >
            <form id="mandant-form" onSubmit={handleSubmit}>
                {error && <div className="login__error">{error}</div>}

                <div className="grid-2">
                    <div className="field">
                        <label>Kundennummer</label>
                        <input type="text" value={data.customer_no || ''} onChange={(e) => update('customer_no', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>Mitgliedsnummer</label>
                        <input type="text" value={data.member_no || ''} onChange={(e) => update('member_no', e.target.value)} />
                    </div>
                </div>

                <div className="field">
                    <label>Name *</label>
                    <input type="text" required value={data.name} onChange={(e) => update('name', e.target.value)} />
                </div>

                <div className="grid-2">
                    <div className="field">
                        <label>Kontaktperson</label>
                        <input type="text" value={data.contact || ''} onChange={(e) => update('contact', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>E-Mail</label>
                        <input type="email" value={data.email || ''} onChange={(e) => update('email', e.target.value)} />
                    </div>
                </div>

                <div className="field">
                    <label>Straße</label>
                    <input type="text" value={data.street || ''} onChange={(e) => update('street', e.target.value)} />
                </div>
                <div className="grid-2">
                    <div className="field">
                        <label>PLZ</label>
                        <input type="text" value={data.zip || ''} onChange={(e) => update('zip', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>Ort</label>
                        <input type="text" value={data.city || ''} onChange={(e) => update('city', e.target.value)} />
                    </div>
                </div>

                <div className="grid-2">
                    <div className="field">
                        <label>Vertrag</label>
                        <input
                            type="text"
                            placeholder="z. B. 2025-01 bis 2026-12"
                            value={data.contract || ''}
                            onChange={(e) => update('contract', e.target.value)}
                        />
                    </div>
                    <div className="field">
                        <label>Laufzeit ab</label>
                        <input type="date" value={data.contract_start || ''} onChange={(e) => update('contract_start', e.target.value)} />
                    </div>
                </div>
                <div className="grid-2">
                    <div className="field">
                        <label>Lizenz</label>
                        <select value={data.license || 'Basis'} onChange={(e) => update('license', e.target.value)}>
                            {LICENSE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div className="field">
                        <label>Kursset</label>
                        <select
                            value={data.course_set || 'Standard'}
                            onChange={(e) => update('course_set', e.target.value)}
                            title="Standard = gemeinsames Kursangebot; die Sondermandanten haben eigene Kurssets"
                        >
                            {courseSets.map((cs) => (
                                <option key={cs} value={cs}>{courseSetLabel(cs)}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={Number(data.active) === 1}
                            onChange={(e) => update('active', e.target.checked ? 1 : 0)}
                            style={{ width: 'auto', margin: 0 }}
                        />
                        Mandant ist aktiv
                    </label>
                </div>

                <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                    Notizen, Rechnungen und Verknüpfungen werden auf der Detail-Seite verwaltet.
                </div>
            </form>
        </Modal>
    );
}
